import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2, TrendingUp, Shield, Users, Building2, Award, Briefcase } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { companyInfo, services, industries, heroImages } from '../data/mockData';

const Home = () => {
  const featuredServices = services.slice(0, 6);

  return (
    <div className="min-h-screen pt-28 md:pt-36">
      {/* Hero Section */}
      <section className="relative h-[600px] flex items-center">
        <div className="absolute inset-0 z-0">
          <img
            src={heroImages.home}
            alt="Business Consultation"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-gray-900/95 via-gray-900/85 to-gray-900/70"></div>
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl">
            <div className="inline-block mb-4">
              <span className="bg-red-600/20 text-red-400 px-4 py-2 rounded-full text-sm font-medium border border-red-600/30">
                UAE's Trusted Business Partner
              </span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight">
              Strategic Excellence.<br />
              <span className="text-red-500">Execution Mastery.</span>
            </h1>
            <p className="text-xl text-gray-300 mb-8 leading-relaxed">
              FinVestra Consulting delivers comprehensive advisory and execution services across Finance, Investment, Strategy, Management, Recruitment, and Business Setup for UAE businesses.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/contact">
                <Button size="lg" className="bg-red-600 hover:bg-red-700 text-white px-8 h-12">
                  Request Consultation
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/services">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-gray-900 h-12 px-8">
                  Explore Services
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Company Introduction */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Your Long-Term Strategic Partner
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed mb-8">
              FinVestra Consulting FZ-LLC is a UAE-based consulting and advisory firm positioned as a premium, execution-focused partner for businesses across industries. We don't just advise—we execute, ensuring your strategic vision translates into measurable results.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              <div className="text-center">
                <div className="text-4xl font-bold text-red-600 mb-2">10+</div>
                <div className="text-sm text-gray-600 font-medium">Service Lines</div>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold text-red-600 mb-2">100%</div>
                <div className="text-sm text-gray-600 font-medium">Client Focused</div>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold text-red-600 mb-2">UAE</div>
                <div className="text-sm text-gray-600 font-medium">Based & Licensed</div>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold text-red-600 mb-2">24/7</div>
                <div className="text-sm text-gray-600 font-medium">Support Available</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Comprehensive Business Solutions
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              From strategic advisory to hands-on execution, we deliver end-to-end services tailored to your business needs.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {featuredServices.map((service) => {
              const IconComponent = {
                TrendingUp, Briefcase, Shield, Users, Building2, Award
              }[service.icon] || TrendingUp;

              return (
                <Card key={service.id} className="group hover:shadow-lg transition-all duration-300 border-gray-200">
                  <CardContent className="p-6">
                    <div className="w-12 h-12 bg-red-50 rounded-lg flex items-center justify-center mb-4 group-hover:bg-red-600 transition-colors">
                      <IconComponent className="w-6 h-6 text-red-600 group-hover:text-white transition-colors" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">{service.title}</h3>
                    <p className="text-gray-600 mb-4 leading-relaxed">{service.description}</p>
                    <Link to="/services" className="text-red-600 font-medium inline-flex items-center hover:text-red-700 transition-colors">
                      Learn More
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </Link>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          <div className="text-center mt-10">
            <Link to="/services">
              <Button size="lg" className="bg-red-600 hover:bg-red-700 text-white">
                View All Services
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Value Propositions */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why Choose FinVestra?
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0">
                <CheckCircle2 className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Execution-Focused Approach</h3>
                <p className="text-gray-600">
                  We don't just advise—we execute alongside you, ensuring strategies translate into tangible outcomes.
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0">
                <CheckCircle2 className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Deep UAE Market Expertise</h3>
                <p className="text-gray-600">
                  Local knowledge, regulatory insight, and established networks across UAE business ecosystem.
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0">
                <CheckCircle2 className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Long-Term Partnership</h3>
                <p className="text-gray-600">
                  We build enduring relationships, invested in your sustained growth and competitive success.
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0">
                <CheckCircle2 className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Compliance & Risk Management</h3>
                <p className="text-gray-600">
                  Rigorous adherence to UAE regulations with proactive risk identification and mitigation.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Industries Served */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Industries We Serve
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Cross-sector expertise delivering specialized solutions across UAE's key industries.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto">
            {industries.map((industry, index) => (
              <div
                key={index}
                className="bg-white p-6 rounded-lg text-center hover:shadow-md transition-shadow border border-gray-200"
              >
                <div className="font-semibold text-gray-900">{industry.name}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-br from-red-600 to-red-700">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to Transform Your Business?
          </h2>
          <p className="text-xl text-red-100 mb-8 max-w-2xl mx-auto">
            Partner with FinVestra for strategic excellence and execution mastery. Let's discuss how we can drive your business forward.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button size="lg" className="bg-white text-red-600 hover:bg-gray-100 px-8 h-12">
                Schedule a Consultation
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <a href={`tel:${companyInfo.phone}`}>
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 h-12 px-8">
                Call Us Now
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
